import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Container, SectionHeading, Btn, Card, Pill } from '../components/ui';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import Icon from '../components/Icon';
import ProductPreview from '../components/ProductPreview';
import CTASection from '../components/CTASection';
import FAQAccordion from '../components/FAQAccordion';
import { FeatureCard, CheckList } from '../components/cards';
import {
  PILLARS, CHANNELS, PAYMENT_METHODS, PAYMENT_FEATURES, REPORTING,
  INTEGRATIONS, SERVICES, FAQ, COMMODITIES, PARSE_USES, BILL_TRACKING,
} from '../data/content';

const PREVIEW_FOR = { acquire: 'parsing', parse: 'parsing', pay: 'payments', report: 'dashboard' };

export default function Platform() {
  const { t } = useLanguage();
  return (
    <div>
      <PageHero
        eyebrow={t('nav.platform')}
        title="One platform for the entire utility lifecycle"
        subtitle="Acquire, parse, validate, pay and report — a single, auditable source of truth for every bill and every dollar, backed by managed services."
      >
        <Btn to="/demo" size="lg" arrow>{t('common.requestDemo')}</Btn>
        <Btn to="/how-it-works" size="lg" variant="outline">{t('nav.howItWorks')}</Btn>
      </PageHero>

      {/* Pillars deep */}
      <section className="px-5 py-16 sm:px-8 sm:py-20">
        <Container className="space-y-16 sm:space-y-24">
          {PILLARS.map((p, idx) => {
            const reversed = idx % 2 === 1;
            const preview = PREVIEW_FOR[p.key];
            return (
              <div key={p.key} className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
                <Reveal className={reversed ? 'lg:order-2' : ''}>
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10"><Icon name={p.icon} className="h-4 w-4" /></span>
                    Step {p.step}
                  </span>
                  <h2 className="mt-4 font-display text-3xl font-bold text-slate-900 dark:text-white">{p.title}</h2>
                  <p className="mt-1 text-base font-medium text-brand">{p.summary}</p>
                  <p className="mt-3 text-base leading-relaxed text-slate-600 dark:text-slate-300">{p.description}</p>
                  <CheckList className="mt-6" items={p.points} />
                </Reveal>
                <Reveal delay={80} className={reversed ? 'lg:order-1' : ''}>
                  {preview ? (
                    <ProductPreview variant={preview} />
                  ) : (
                    <Card className="grid grid-cols-2 gap-3">
                      {p.points.map((pt) => (
                        <div key={pt} className="rounded-xl bg-slate-50 p-4 text-sm font-medium text-slate-700 dark:bg-white/[0.04] dark:text-slate-200">{pt}</div>
                      ))}
                    </Card>
                  )}
                </Reveal>
              </div>
            );
          })}
        </Container>
      </section>

      {/* Commodities & data captured */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="Every commodity" title="Accurate data capture across every commodity" subtitle="AI Agents capture charges, usage and meter reads across all your utilities — validated against the figures on the page, ready for payments, reporting and budgeting." /></Reveal>
          <div className="mt-10 flex flex-wrap justify-center gap-2.5">
            {COMMODITIES.map((c) => (<Pill key={c.name}><Icon name={c.icon} className="h-3.5 w-3.5 text-brand" /> {c.name}</Pill>))}
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PARSE_USES.map((u, i) => (<Reveal key={u.title} delay={i * 60}><FeatureCard icon={u.icon} title={u.title} text={u.text} /></Reveal>))}
          </div>
        </Container>
      </section>

      {/* Bill tracking & completeness */}
      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="Completeness" title="Never lose track of a bill" subtitle="Active tracking of missing, late-arriving and overdue bills keeps your data complete and your payments on time — especially during a transition from a previous incumbent." /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {BILL_TRACKING.map((b, i) => (<Reveal key={b.title} delay={i * 60}><FeatureCard icon={b.icon} title={b.title} text={b.text} /></Reveal>))}
          </div>
        </Container>
      </section>

      {/* Channels */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow={t('home.channels.eyebrow')} title={t('home.channels.title')} subtitle={t('home.channels.subtitle')} /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CHANNELS.map((c, i) => (<Reveal key={c.title} delay={i * 60}><FeatureCard {...c} /></Reveal>))}
          </div>
        </Container>
      </section>

      {/* Payments */}
      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <SectionHeading align="left" eyebrow={t('home.payments.eyebrow')} title={t('home.payments.title')} subtitle={t('home.payments.subtitle')} />
              <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {PAYMENT_FEATURES.map((f) => (
                  <div key={f.title} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
                    <div className="flex items-center gap-2"><Icon name={f.icon} className="h-4.5 w-4.5 text-brand" /><span className="text-sm font-semibold text-slate-900 dark:text-white">{f.title}</span></div>
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{f.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {PAYMENT_METHODS.map((m) => (<Pill key={m.title}><Icon name={m.icon} className="h-3.5 w-3.5 text-brand" /> {m.title}</Pill>))}
              </div>
            </Reveal>
            <Reveal delay={80}><ProductPreview variant="payments" title="pay.billportal.io" /></Reveal>
          </div>
        </Container>
      </section>

      {/* Reporting */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow={t('home.reporting.eyebrow')} title={t('home.reporting.title')} subtitle={t('home.reporting.subtitle')} /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {REPORTING.map((r, i) => (<Reveal key={r.title} delay={i * 60}><FeatureCard icon={r.icon} title={r.title} text={r.text} /></Reveal>))}
          </div>
        </Container>
      </section>

      {/* Managed services */}
      <section className="px-5 py-16 sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow="Managed services" title="Software plus a team that runs it with you" subtitle="BillPortal pairs the platform with managed services so your team focuses on exceptions, not data entry." /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (<Reveal key={s.title} delay={i * 60}><FeatureCard {...s} /></Reveal>))}
          </div>
        </Container>
      </section>

      {/* Integrations */}
      <section className="bg-slate-50 px-5 py-16 dark:bg-white/[0.02] sm:px-8 sm:py-24">
        <Container>
          <Reveal><SectionHeading eyebrow={t('home.integrations.eyebrow')} title={t('home.integrations.title')} subtitle={t('home.integrations.subtitle')} /></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {INTEGRATIONS.map((it, i) => (
              <Reveal key={it.name} delay={i * 60}>
                <Card className="flex items-center gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand"><Icon name={it.icon} className="h-5 w-5" /></span>
                  <div><div className="text-sm font-semibold text-slate-900 dark:text-white">{it.name}</div><div className="text-xs text-slate-500 dark:text-slate-400">{it.detail}</div></div>
                </Card>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="px-5 py-16 sm:px-8 sm:py-20">
        <Container>
          <Reveal><SectionHeading eyebrow="FAQ" title="Frequently asked questions" /></Reveal>
          <Reveal delay={80} className="mt-10"><FAQAccordion items={FAQ} /></Reveal>
        </Container>
      </section>

      <CTASection />
    </div>
  );
}
